import { fbm2, hash32 } from '@vm/shared';
import { M } from '../palette';
import { blocks, MapBuilder, planks, smoothField, solid, symPoint, symYaw, yawToward, type Brush } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
const SYM = 'mirrorX' as const;
/** Central compound (fenced yard around the farmhouse), half-open voxel bounds. */
const C0 = 216;
const C1 = 424;

function terrain(b: MapBuilder): void {
  const h = new Int16Array(S * S);
  const surface = new Uint8Array(S * S);
  const tint = smoothField(S, S, 8, (x, z) => fbm2(x * 0.01, z * 0.01, 601, 2));
  const rise = smoothField(S, S, 8, (x, z) => fbm2(x * 0.012, z * 0.012, 602, 2));
  const g1 = M('grass_1');
  const g2 = M('grass_2');
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const mx = x < S / 2 ? x : S - 1 - x;
      // Gentle banks toward the wooded edges; flat farmland inside.
      // The west/east edges stay flat where the team yards and the main road are.
      const yard = z > 250 && z < 380 ? 1 : 0;
      const edge = Math.min(yard ? 999 : mx, z, S - 1 - z);
      const bank = edge < 48 ? Math.floor(((48 - edge) / 48) * (4 + rise(mx, z) * 10) / 3) * 3 : 0;
      h[x + z * S] = G + bank;
      surface[x + z * S] = tint(mx, z) > 0.55 ? g2 : g1;
    }
  b.heightfield({ height: h, surface, sub: [M('dirt_1'), M('dirt_2')], subDepth: 4, deep: M('rock_1') });
}

/** Scarecrow on a pole. */
function scarecrow(b: MapBuilder, x: number, z: number): void {
  b.box(x, G, z, x + 2, G + 26, z + 2, M('log_2'));
  b.box(x - 8, G + 18, z, x + 10, G + 20, z + 2, M('log_2'));
  b.box(x - 3, G + 12, z - 1, x + 5, G + 22, z + 3, M('cloth_blue'));
  b.box(x - 7, G + 17, z - 1, x - 3, G + 21, z + 3, M('cloth_blue'));
  b.box(x + 5, G + 17, z - 1, x + 9, G + 21, z + 3, M('cloth_blue'));
  b.box(x - 2, G + 22, z - 2, x + 4, G + 28, z + 4, M('hay'));
  b.box(x - 4, G + 28, z - 4, x + 6, G + 29, z + 6, M('hay'));
  b.box(x - 1, G + 29, z - 1, x + 3, G + 32, z + 3, M('hay'));
  b.box(x - 1, G + 25, z + 4, x, G + 26, z + 5, M('paint_black'));
  b.box(x + 2, G + 25, z + 4, x + 3, G + 26, z + 5, M('paint_black'));
}

/** Farm tractor with big rear wheels. */
function tractor(b: MapBuilder, x: number, z: number, color: number): void {
  b.box(x, G + 4, z + 2, x + 30, G + 12, z + 14, color);
  b.box(x + 18, G + 12, z + 2, x + 30, G + 26, z + 14, color);
  b.box(x + 19, G + 14, z + 3, x + 29, G + 24, z + 13, M('glass_tint'));
  b.box(x + 18, G + 26, z + 1, x + 31, G + 27, z + 15, M('paint_white'));
  b.box(x + 4, G + 12, z + 7, x + 6, G + 20, z + 9, M('steel_dark_1'));
  for (const zz of [z - 2, z + 12]) {
    b.box(x + 20, G, zz, x + 32, G + 12, zz + 6, M('rubber'));
    b.box(x + 2, G, zz + 1, x + 10, G + 8, zz + 5, M('rubber'));
  }
}

/** Hay wagon (along z): plank bed on four wheels with a hay load; blocks sightlines down the road. */
function hayWagon(b: MapBuilder, x: number, z: number): void {
  b.box(x, G + 4, z, x + 18, G + 6, z + 30, M('planks_4'));
  for (const zz of [z + 3, z + 22]) for (const xx of [x - 1, x + 17]) b.box(xx, G, zz, xx + 2, G + 7, zz + 6, M('planks_dark_2'));
  b.box(x + 1, G + 6, z + 1, x + 17, G + 16, z + 29, M('hay'));
  b.box(x + 3, G + 16, z + 4, x + 15, G + 20, z + 26, M('hay'));
  b.box(x + 18, G + 5, z + 13, x + 30, G + 6, z + 17, M('log_2'));
}

/** Wooden chicken coop on short legs with a ramp. */
function coop(b: MapBuilder, x: number, z: number): void {
  for (const [dx, dz] of [[0, 0], [22, 0], [0, 16], [22, 16]]) b.box(x + dx, G, z + dz, x + dx + 2, G + 6, z + dz + 2, M('log_2'));
  b.box(x, G + 6, z, x + 24, G + 20, z + 18, planks([M('planks_1')], 0));
  b.clear(x + 1, G + 7, z + 1, x + 23, G + 19, z + 17);
  b.box(x - 1, G + 20, z - 1, x + 25, G + 22, z + 19, M('roof_red_2'));
  b.clear(x + 8, G + 7, z + 17, x + 14, G + 13, z + 18);
  for (let i = 0; i < 6; i++) b.box(x + 8, G + i, z + 18 + i, x + 14, G + i + 1, z + 19 + i, M('planks_3'));
}

function westHalf(b: MapBuilder): void {
  const fenceB: Brush = planks([M('planks_2')], 0);
  // Dirt lanes: the main road from the west gate to the compound, a lane north to the barn.
  b.box(0, G - 1, 302, C0, G, 338, solid([M('dirt_3')]));
  b.box(100, G - 1, 230, 124, G, 302, solid([M('dirt_3')]));

  // Red barn with a hay loft, big doors toward the lane, and a silo beside it.
  b.building({
    x0: 50, z0: 120, x1: 170, z1: 230, y0: G - 1, floors: 1, floorH: 48, wall: planks([M('paint_red')], 0), roof: M('roof_slate'),
    roofStyle: 'gableX', trim: M('stone_4'), windows: { w: 8, h: 8, sill: 28, spacing: 22, glass: null },
    doors: [{ side: 's', w: 30, h: 36 }, { side: 'n', w: 14 }, { side: 'e', w: 12 }],
  });
  // White trim around the big door, loft floor with a ladder-stair, hay stacks up top.
  b.box(94, G, 229, 96, G + 38, 231, M('paint_white'));
  b.box(126, G, 229, 128, G + 38, 231, M('paint_white'));
  b.box(94, G + 37, 229, 128, G + 39, 231, M('paint_white'));
  b.box(54, G + 24, 124, 94, G + 26, 226, planks([M('planks_2')], 0));
  b.stairs(94, G, 200, 'n', 12, 25, planks([M('planks_3')], 0), 3);
  b.clear(94, G + 24, 174, 106, G + 27, 200);
  for (const [x, z] of [[58, 130], [70, 130], [58, 140], [64, 135], [58, 200], [70, 206]]) b.haybale(x, G + 26, z);
  b.cylinder(196, 170, G, G + 92, 15, M('metal_3'));
  b.cylinder(196, 170, G + 1, G + 91, 12, 0);
  b.dome(196, G + 92, 170, 15, M('roof_red_2'));
  b.clear(180, G, 164, 186, G + 22, 176);
  for (let y = G + 6; y < G + 88; y += 3) b.box(208, y, 169, 211, y + 1, 171, M('steel_dark_1'));
  b.box(208, G, 168, 209, G + 90, 169, M('steel_dark_1'));
  b.box(210, G, 172, 211, G + 90, 173, M('steel_dark_1'));

  // Wheat field (knee-high cover) with fences, and a pumpkin patch with a scarecrow.
  for (let z = 364; z < 470; z += 6) b.box(40, G, z, 190, G + 7, z + 4, M('wheat'));
  for (let z = 364; z < 470; z += 6) b.box(40, G - 1, z + 4, 190, G, z + 6, M('dirt_2'));
  b.fence(36, 358, 196, 358, G, fenceB);
  b.fence(36, 474, 196, 474, G, fenceB);
  for (let z = 504; z < 590; z += 10)
    for (let x = 50; x < 180; x += 12) {
      const hh = hash32(x, z, 61);
      b.box(x, G - 1, z, x + 10, G, z + 6, M('dirt_2'));
      if (hh % 3 !== 0) b.box(x + 2, G, z + 1, x + 7 + (hh % 2), G + 4 + (hh % 3), z + 5, M('pumpkin'));
      b.box(x + 4, G + 4 + (hh % 3), z + 2, x + 5, G + 6 + (hh % 3), z + 3, M('leaves_1'));
    }
  scarecrow(b, 114, 540);
  b.fence(44, 496, 186, 496, G, fenceB);

  // Coop, tractor, hay, water trough and crates around the yard.
  coop(b, 150, 260);
  tractor(b, 30, 236, M('paint_green'));
  for (const [x, z, alongX] of [[190, 250, true], [190, 262, true], [196, 256, false]] as const) b.haybale(x, z === 256 ? G + 8 : G, z, alongX);
  b.box(60, G, 272, 90, G + 6, 280, planks([M('planks_dark_1')], 0));
  b.box(61, G + 1, 273, 89, G + 5, 279, M('water'));
  b.crate(170, G, 346);
  b.crate(178, G, 356, 8);
  // Hay wagon parked on the road in front of the yard: cover from the long sightline.
  hayWagon(b, 110, 306);
  b.tntBarrel(34, G, 204);

  // Woods along the north and south edges and the west side, with bushes between trunks.
  const trees: [number, number, number][] = [
    [20, 20, 44], [70, 30, 40], [130, 20, 46], [190, 36, 38], [250, 22, 44], [300, 30, 40], [30, 80, 38], [220, 90, 36],
    [20, 610, 44], [80, 600, 40], [150, 620, 46], [210, 604, 38], [270, 616, 42], [26, 540, 40], [26, 420, 38], [22, 120, 40],
  ];
  for (const [x, z, hh] of trees) {
    const y = b.surfaceY(x, z);
    if ((x + z) % 3 === 0) b.pineTree(x, y, z, hh + 14);
    else b.leafyTree(x, y, z, hh, 12, (x + z) % 2 ? M('leaves_2') : M('leaves_1'));
  }
  for (const [x, z] of [[46, 54], [104, 60], [160, 50], [270, 66], [50, 588], [118, 580], [240, 590], [46, 460]]) b.bush(x, b.surfaceY(x, z), z, 6, M('leaves_3'));
  // Pond in the south-west corner of the farmland.
  for (let z = 470; z < 560; z++)
    for (let x = 196; x < 300; x++) {
      const d = Math.hypot((x - 250) / 1.4, z - 520);
      if (d > 34) continue;
      if (d > 30) {
        b.box(x, G - 1, z, x + 1, G, z + 1, M('sand_3'));
        continue;
      }
      b.clear(x, G - 6, z, x + 1, G, z + 1);
      b.box(x, G - 7, z, x + 1, G - 6, z + 1, M('mud'));
      b.box(x, G - 6, z, x + 1, G - 1, z + 1, M('water'));
    }
  for (const [x, z] of [[222, 500], [236, 540], [280, 510]]) b.box(x, G - 1, z, x + 4, G, z + 4, M('leaves_4'));
}

/** Farmhouse compound on the center line (symmetric by design). */
function compound(b: MapBuilder): void {
  const stone: Brush = blocks([M('stone_1'), M('cobble_1')], 3, 7);
  // Low fieldstone wall around the yard, gates on all four sides.
  b.walls(C0, G, C0, C1, G + 12, C1, 4, stone);
  b.box(C0, G + 12, C0, C1, G + 13, C1, M('stone_2'));
  b.clear(C0 + 4, G + 12, C0 + 4, C1 - 4, G + 13, C1 - 4);
  for (const [x0, z0, x1, z1] of [[300, C0, 340, C0 + 4], [300, C1 - 4, 340, C1], [C0, 300, C0 + 4, 340], [C1 - 4, 300, C1, 340]]) b.clear(x0, G, z0, x1, G + 14, z1);
  for (const [x, z] of [[296, C0 - 1], [340, C0 - 1], [296, C1 - 5], [340, C1 - 5], [C0 - 1, 296], [C0 - 1, 340], [C1 - 5, 296], [C1 - 5, 340]])
    b.box(x, G, z, x + 6, G + 18, z + 6, M('log_2'));
  b.box(C0 + 4, G - 1, C0 + 4, C1 - 4, G, C1 - 4, solid([M('dirt_3')]));

  // The farmhouse: two floors, porch, chimney.
  b.building({
    x0: 256, z0: 236, x1: 384, z1: 316, y0: G - 1, floors: 2, wall: planks([M('plaster_3')], 0), roof: M('roof_red_1'), roofStyle: 'gable',
    trim: M('stone_2'), floor: planks([M('planks_2')], 0), windows: { w: 8, h: 10, sill: 9, spacing: 18, glass: M('glass') },
    doors: [{ side: 's', w: 12 }, { side: 'w', w: 10 }, { side: 'e', w: 10 }],
  });
  b.box(284, G + 24, 316, 356, G + 26, 330, M('roof_red_2'));
  for (const x of [284, 318, 353]) b.box(x, G, 327, x + 3, G + 24, 330, M('log_1'));
  b.box(284, G, 316, 356, G + 1, 330, planks([M('planks_3')], 0));
  b.box(360, G + 50, 250, 370, G + 80, 260, blocks([M('brick_red_1')], 3, 0));
  // Windmill in the south yard: a hollow stone tower (a hiding spot with a door) with four sails
  // over the south gate.
  const wx = 320;
  const wz = 380;
  b.octagon(wx, wz, G, G + 60, 11, blocks([M('stone_3')], 3, 2));
  b.octagon(wx, wz, G, G + 58, 8, 0);
  b.clear(wx - 4, G, wz - 12, wx + 4, G + 22, wz - 8);
  b.box(wx - 8, G - 1, wz - 8, wx + 9, G, wz + 9, M('planks_2'));
  b.cone(wx, G + 60, wz, 13, 14, M('roof_slate'));
  b.box(wx - 2, G + 50, wz + 11, wx + 3, G + 55, wz + 16, M('log_2'));
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    for (let k = 4; k < 34; k++) {
      const px = wx + dx * k;
      const py = G + 52 + dy * k;
      b.box(px - (dy !== 0 ? 3 : 0), py - (dx !== 0 ? 3 : 0), wz + 16, px + 1 + (dy !== 0 ? 3 : 0), py + 1 + (dx !== 0 ? 3 : 0), wz + 17, k % 6 === 0 ? M('log_1') : M('cloth_white'));
    }
    b.line(wx, G + 52, wz + 17, wx + dx * 34, G + 52 + dy * 34, wz + 17, M('log_1'));
  }
  // Well, crates and sandbag nests for holding out.
  b.cylinder(262, 360, G, G + 7, 7, stone);
  b.cylinder(262, 360, G + 1, G + 7, 4.5, M('water'));
  for (const dx of [-6, 5]) b.box(262 + dx, G + 7, 359, 263 + dx, G + 22, 361, M('log_1'));
  b.box(254, G + 22, 354, 271, G + 24, 367, M('roof_red_2'));
  b.crate(372, G, 350);
  b.crate(380, G, 362, 8);
  b.crate(373, G + 10, 352, 8);
  b.sandbags(236, 396, 270, 404, G, 9);
  b.sandbags(370, 396, 404, 404, G, 9);
  b.lamp(240, G, 240, 34);
  b.lamp(400, G, 240, 34);
}

function build(b: MapBuilder): MapMeta {
  terrain(b);
  westHalf(b);
  b.symmetrize(SYM);
  compound(b);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const team0: [number, number][] = [
    [3, 27], [3, 29.5], [3, 32], [3, 34.5], [5.2, 27], [5.2, 29.5], [5.2, 32], [5.2, 34.5],
  ];
  for (const [x, z] of team0) {
    const yaw = -Math.PI / 2;
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  // FFA spawns: the first eight are inside the farmhouse compound (survivor spawns for the
  // zombie mode), the rest spread over the farm.
  const ffa: [number, number][] = [
    [24.5, 25], [39.5, 25], [24.5, 35.5], [39.5, 35.5], [27, 39.5], [37, 39.5], [23.5, 30], [40.5, 30],
    [12, 25], [52, 25], [10, 49], [54, 49], [18, 59], [46, 59], [20, 9], [44, 9], [28, 8], [36, 8], [9, 34], [55, 34],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 4.5), z, yaw: yawToward(x, z, 32, 32) });
  // Zombies pour out of the woods and the far corners of the fields.
  const enemies: [number, number][] = [
    [4, 3], [16, 3.5], [32, 3], [48, 3.5], [60, 3], [4, 61], [16, 60.5], [48, 60.5], [60, 61], [1.5, 45], [62.5, 45], [1.5, 18], [62.5, 18],
  ];
  for (const [x, z] of enemies) meta.enemySpawns.push({ x, y: b.standAt(x, z, 8), z });
  meta.points.push({ id: 'A', name: 'West Barn', x: 11, y: gy, z: 26, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Farmhouse Yard', x: 32, y: gy, z: 35, r: 3.5 });
  meta.points.push({ id: 'C', name: 'East Barn', x: 53, y: gy, z: 26, r: 3.5 });
  for (const [x, z, kind] of [
    [26, 34, 'health'], [38, 34, 'health'], [32, 25, 'armor'], [24, 39, 'ammo'], [40, 39, 'ammo'], [11, 17, 'health'], [53, 17, 'health'],
  ] as const)
    meta.pickups.push({ kind, x, y: b.standAt(x, z, 5), z });
  return meta;
}

export const harvestFarm: MapDef = {
  id: 'harvest_farm',
  name: 'Harvest Farm',
  description: 'A quiet farm at dusk: red barns, golden wheat and a walled farmhouse yard to hold when the corrupted come out of the woods.',
  size: [S, 192, S],
  modes: ['zombies', 'ffa', 'tdm', 'sandbox', 'practice'],
  players: [1, 12],
  seed: 3131,
  version: 2,
  theme: {
    skyTop: 0x3e4f8a,
    skyHorizon: 0xf0a868,
    fog: 0xc89878,
    fogNear: 34,
    fogFar: 120,
    sun: [-0.7, 0.32, 0.25],
    sunColor: 0xffb070,
    sunIntensity: 0.85,
    ambient: 0x9aa0c8,
    ambientIntensity: 0.5,
    outside: 0x4a7a32,
    music: 'farm',
  },
  build,
};
